import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Checkbox,
  IconButton,
  Collapse,
  Menu,
  MenuItem,
  ListItemText,
  Divider,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Alert,
  Link,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RefreshIcon from '@mui/icons-material/Refresh';
import PaidIcon from '@mui/icons-material/Paid';
import DescriptionIcon from '@mui/icons-material/Description';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import StarIcon from '@mui/icons-material/Star';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import { useNotify, useGetList } from 'react-admin';
import { useParams, useNavigate } from 'react-router-dom';
import {
  apiFetch,
  money,
  formatDate,
  PartnerInvoice,
  InvoiceCase,
  MatchStatus,
  PartnerType,
  PARTNER_TYPE_ROLE_FIELD,
  PARTNER_TYPE_LABELS,
} from './api';

const MATCH_COLOR: Record<MatchStatus, 'success' | 'warning' | 'default' | 'info'> = {
  matched: 'success',
  manual: 'info',
  ambiguous: 'warning',
  unmatched: 'default',
};

const MATCH_LABEL: Record<MatchStatus, string> = {
  matched: 'Matched',
  manual: 'Manual',
  ambiguous: 'Review',
  unmatched: 'No match',
};

export const InvoiceDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const notify = useNotify();
  const [invoice, setInvoice] = useState<PartnerInvoice | null>(null);
  const [loading, setLoading] = useState(true);
  const [rematching, setRematching] = useState(false);
  const [payOpen, setPayOpen] = useState(false);
  const [editPartnerOpen, setEditPartnerOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const data = await apiFetch(`partner-invoices/${id}`);
      setInvoice(data);
    } catch {
      notify('Failed to load invoice', { type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [id, notify]);

  useEffect(() => {
    void load();
  }, [load]);

  const patchCase = async (caseId: string, body: Record<string, any>) => {
    try {
      const updated = await apiFetch(`partner-invoices/cases/${caseId}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      });
      setInvoice(updated);
    } catch {
      notify('Update failed', { type: 'error' });
    }
  };

  const handleReconcileAll = async (reconciled: boolean) => {
    try {
      const updated = await apiFetch(`partner-invoices/${id}/reconcile-all`, {
        method: 'POST',
        body: JSON.stringify({ reconciled }),
      });
      setInvoice(updated);
    } catch {
      notify('Update failed', { type: 'error' });
    }
  };

  const handleRematch = async () => {
    setRematching(true);
    try {
      const updated = await apiFetch(`partner-invoices/${id}/rematch`, { method: 'POST' });
      setInvoice(updated);
      notify('Re-matched', { type: 'success' });
    } catch {
      notify('Re-match failed', { type: 'error' });
    } finally {
      setRematching(false);
    }
  };

  const handleDelete = async () => {
    try {
      await apiFetch(`partner-invoices/${id}`, { method: 'DELETE' });
      notify('Invoice deleted', { type: 'success' });
      navigate('/partner-reconciliation');
    } catch {
      notify('Delete failed', { type: 'error' });
    }
  };

  const handleViewFiles = async () => {
    try {
      const { files } = await apiFetch(`partner-invoices/${id}/file?redirect=false`);
      (files || []).forEach((f: { url: string }) => window.open(f.url, '_blank'));
    } catch {
      notify('Could not open file', { type: 'error' });
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress />
      </Box>
    );
  }
  if (!invoice) return null;

  const cases = invoice.cases || [];
  const reconciledCount = cases.filter((c) => c.reconciled).length;

  return (
    <Box p={3}>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/partner-reconciliation')} sx={{ mb: 2 }}>
        Back to invoices
      </Button>

      {invoice.status === 'failed' && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Extraction failed: {invoice.extraction_error || 'unknown error'}
        </Alert>
      )}

      {/* Header card */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={2}>
          <Box>
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="h5" color={invoice.partner?.name ? 'inherit' : 'text.secondary'}>
                {invoice.partner?.name || 'Set partner…'}
              </Typography>
              <IconButton size="small" onClick={() => setEditPartnerOpen(true)} title="Change partner">
                <EditIcon fontSize="small" />
              </IconButton>
            </Box>
            <Typography variant="body2" color="text.secondary">
              {invoice.partner_type ? PARTNER_TYPE_LABELS[invoice.partner_type] : 'Transport company'}
              {invoice.invoice_number ? ` · Invoice ${invoice.invoice_number}` : ''}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {invoice.period_start && invoice.period_end
                ? `Period ${formatDate(invoice.period_start)} – ${formatDate(invoice.period_end)}`
                : `Invoice date ${formatDate(invoice.invoice_date)}`}
            </Typography>
            {invoice.file_name && (
              <Link component="button" variant="body2" onClick={() => void handleViewFiles()} sx={{ mt: 1, display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                <DescriptionIcon fontSize="small" />
                {(invoice.source_files?.length ?? 0) > 1
                  ? `${invoice.source_files!.length} files`
                  : invoice.file_name}
              </Link>
            )}
          </Box>

          <Box textAlign="right">
            <Box display="flex" gap={4} justifyContent="flex-end" mb={1}>
              <Box>
                <Typography variant="caption" color="text.secondary">Subtotal</Typography>
                <Typography variant="body1">{money(invoice.extracted_subtotal, invoice.currency)}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Tax</Typography>
                <Typography variant="body1">{money(invoice.extracted_tax, invoice.currency)}</Typography>
                {invoice.tax_breakdown && (
                  <Typography variant="caption" color="text.secondary" display="block">
                    {Object.entries(invoice.tax_breakdown)
                      .map(([k, v]) => `${k.toUpperCase()} ${money(v, invoice.currency)}`)
                      .join(' · ')}
                  </Typography>
                )}
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Total</Typography>
                <Typography variant="h6">{money(invoice.extracted_total, invoice.currency)}</Typography>
              </Box>
            </Box>
            {invoice.total_matches_extracted === false && (
              <Tooltip title={`Line items + tax (${money(invoice.computed_total, invoice.currency)}) don't match the invoice total (${money(invoice.extracted_total, invoice.currency)})`}>
                <Chip size="small" color="warning" icon={<WarningAmberIcon />} label="Totals mismatch" />
              </Tooltip>
            )}
            {invoice.total_matches_extracted === true && (
              <Chip size="small" color="success" variant="outlined" icon={<CheckCircleIcon />} label="Totals check out" />
            )}
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
          <Typography variant="body2" color="text.secondary">
            {reconciledCount}/{cases.length} cases reconciled
          </Typography>
          <Box display="flex" gap={1}>
            <Button
              startIcon={<DoneAllIcon />}
              onClick={() => void handleReconcileAll(reconciledCount < cases.length)}
              disabled={cases.length === 0}
            >
              {reconciledCount === cases.length && cases.length > 0 ? 'Clear all' : 'Reconcile all'}
            </Button>
            <Button
              startIcon={rematching ? <CircularProgress size={16} /> : <RefreshIcon />}
              onClick={() => void handleRematch()}
              disabled={rematching}
            >
              Re-match
            </Button>
            {invoice.status === 'paid' ? (
              <Chip
                color="success"
                icon={<PaidIcon />}
                label={`Paid${invoice.payment_method ? ` · ${invoice.payment_method === 'cheque' ? 'Cheque' : 'Credit card'}` : ''}${
                  invoice.payment_reference ? ` · ${invoice.payment_reference}` : ''
                }`}
              />
            ) : (
              <Button variant="contained" startIcon={<PaidIcon />} onClick={() => setPayOpen(true)}>
                Mark paid
              </Button>
            )}
            <Button color="error" startIcon={<DeleteOutlineIcon />} onClick={() => setDeleteOpen(true)}>
              Delete
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Cases */}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 40 }} />
              <TableCell>Deceased</TableCell>
              <TableCell>Ref</TableCell>
              <TableCell>Service date</TableCell>
              <TableCell align="right">Case total</TableCell>
              <TableCell>Matched case</TableCell>
              <TableCell align="center">Reconciled</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {cases.map((c) => (
              <CaseRow
                key={c.id}
                invoiceCase={c}
                currency={invoice.currency}
                onReconcile={(checked) => void patchCase(c.id, { reconciled: checked })}
                onMatch={(funeralId) => void patchCase(c.id, { matched_funeral_id: funeralId })}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <MarkPaidDialog
        open={payOpen}
        onClose={() => setPayOpen(false)}
        onConfirm={async (body) => {
          try {
            const updated = await apiFetch(`partner-invoices/${id}/pay`, { method: 'POST', body: JSON.stringify(body) });
            setInvoice(updated);
            notify('Marked paid', { type: 'success' });
            setPayOpen(false);
          } catch {
            notify('Failed to mark paid', { type: 'error' });
          }
        }}
      />

      <EditPartnerDialog
        open={editPartnerOpen}
        invoice={invoice}
        onClose={() => setEditPartnerOpen(false)}
        onConfirm={async (body) => {
          try {
            const updated = await apiFetch(`partner-invoices/${id}`, { method: 'PATCH', body: JSON.stringify(body) });
            setInvoice(updated);
            notify('Partner updated', { type: 'success' });
            setEditPartnerOpen(false);
          } catch {
            notify('Failed to update partner', { type: 'error' });
          }
        }}
      />

      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete this invoice?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            This permanently removes the invoice, its {invoice.cases?.length ?? 0} cases, and the
            uploaded file{(invoice.source_files?.length ?? 0) > 1 ? 's' : ''}. This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)}>Cancel</Button>
          <Button color="error" variant="contained" startIcon={<DeleteOutlineIcon />} onClick={() => void handleDelete()}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// --- one case row, expandable to show line items ----------------------------

const CaseRow = ({
  invoiceCase: c,
  currency,
  onReconcile,
  onMatch,
}: {
  invoiceCase: InvoiceCase;
  currency: string;
  onReconcile: (checked: boolean) => void;
  onMatch: (funeralId: number | null) => void;
}) => {
  const [open, setOpen] = useState(false);

  return (
    <>
      <TableRow hover sx={c.reconciled ? { bgcolor: '#f1f8f2' } : {}}>
        <TableCell>
          <IconButton size="small" onClick={() => setOpen((o) => !o)}>
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell sx={{ fontWeight: 'bold' }}>
          {c.deceased_name_raw}
          {c.duplicate_in_invoice && (
            <Tooltip title="This case appears more than once on this invoice">
              <Chip size="small" color="error" icon={<WarningAmberIcon />} label="Dup on invoice" sx={{ ml: 1 }} />
            </Tooltip>
          )}
          {(c.duplicate_other_invoices?.length ?? 0) > 0 && (
            <Tooltip
              title={`Already billed on another invoice from this partner${
                c.duplicate_other_invoices?.some((o) => o.status === 'paid') ? ' (one is already paid)' : ''
              }`}
            >
              <Chip
                size="small"
                color={c.duplicate_other_invoices?.some((o) => o.status === 'paid') ? 'error' : 'warning'}
                icon={<WarningAmberIcon />}
                label="Billed before"
                sx={{ ml: 1 }}
              />
            </Tooltip>
          )}
        </TableCell>
        <TableCell>{c.partner_case_ref || '—'}</TableCell>
        <TableCell>{formatDate(c.service_date)}</TableCell>
        <TableCell align="right">{money(c.case_total, currency)}</TableCell>
        <TableCell>
          <MatchCell invoiceCase={c} onMatch={onMatch} />
        </TableCell>
        <TableCell align="center">
          <Checkbox checked={c.reconciled} onChange={(e) => onReconcile(e.target.checked)} />
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell sx={{ py: 0, border: 0 }} colSpan={7}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ m: 1, ml: 6 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Line item</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell align="right">Tax</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(c.line_items || []).map((li) => (
                    <TableRow key={li.id}>
                      <TableCell>{li.description}</TableCell>
                      <TableCell align="right">{money(li.amount, currency)}</TableCell>
                      <TableCell align="right">{money(li.tax, currency)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

// --- match chip + candidate picker ------------------------------------------

const MatchCell = ({
  invoiceCase: c,
  onMatch,
}: {
  invoiceCase: InvoiceCase;
  onMatch: (funeralId: number | null) => void;
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [manualOpen, setManualOpen] = useState(false);
  const candidates = c.match_candidates || [];
  const matchedName = c.matched_funeral?.deceased_client?.name;

  return (
    <>
      <Box display="flex" alignItems="center" gap={1}>
        <Chip
          size="small"
          color={MATCH_COLOR[c.match_status]}
          variant={c.match_status === 'unmatched' ? 'outlined' : 'filled'}
          label={
            c.matched_funeral_id
              ? `#${c.matched_funeral_id}${matchedName ? ` · ${matchedName}` : ''}`
              : MATCH_LABEL[c.match_status]
          }
          onClick={(e) => setAnchorEl(e.currentTarget)}
          sx={{ cursor: 'pointer' }}
        />
        {c.match_confidence != null && c.matched_funeral_id && (
          <Typography variant="caption" color="text.secondary">
            {c.match_confidence}%
          </Typography>
        )}
      </Box>

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
        {candidates.length === 0 && (
          <MenuItem disabled>
            <ListItemText>No candidates found</ListItemText>
          </MenuItem>
        )}
        {candidates.map((cand) => (
          <MenuItem
            key={cand.funeral_id}
            selected={cand.funeral_id === c.matched_funeral_id}
            onClick={() => {
              onMatch(cand.funeral_id);
              setAnchorEl(null);
            }}
          >
            {cand.partner_assigned && (
              <Tooltip title="This partner is assigned to this case">
                <StarIcon fontSize="small" color="warning" sx={{ mr: 1 }} />
              </Tooltip>
            )}
            <ListItemText
              primary={`#${cand.funeral_id} · ${cand.deceased_name}`}
              secondary={`${formatDate(cand.date_of_death)} · ${Math.round(cand.similarity * 100)}% name match`}
            />
          </MenuItem>
        ))}
        <Divider />
        <MenuItem
          onClick={() => {
            setManualOpen(true);
            setAnchorEl(null);
          }}
        >
          <ListItemText>Search for a case…</ListItemText>
        </MenuItem>
        {c.matched_funeral_id && (
          <MenuItem
            onClick={() => {
              onMatch(null);
              setAnchorEl(null);
            }}
          >
            <ListItemText>Clear match</ListItemText>
          </MenuItem>
        )}
      </Menu>

      <ManualMatchDialog
        open={manualOpen}
        onClose={() => setManualOpen(false)}
        onConfirm={(funeralId) => {
          onMatch(funeralId);
          setManualOpen(false);
        }}
      />
    </>
  );
};

interface FuneralSearchResult {
  id: number;
  name: string;
  date_of_death: string | null;
}

const ManualMatchDialog = ({
  open,
  onClose,
  onConfirm,
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: (funeralId: number) => void;
}) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<FuneralSearchResult[]>([]);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    if (open) {
      setQuery('');
      setResults([]);
    }
  }, [open]);

  // Debounced name search; a bare number also matches a funeral ID directly.
  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) {
      setResults([]);
      return;
    }
    setSearching(true);
    const handle = setTimeout(() => {
      void apiFetch(`partner-invoices/funerals/search?q=${encodeURIComponent(q)}`)
        .then((rows) => setResults(rows || []))
        .catch(() => setResults([]))
        .finally(() => setSearching(false));
    }, 300);
    return () => clearTimeout(handle);
  }, [query]);

  const numericId = /^\d+$/.test(query.trim()) ? Number(query.trim()) : null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Match to a case</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          fullWidth
          label="Search by deceased name (or enter a funeral ID)"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          sx={{ mt: 1, mb: 1 }}
        />
        {searching && (
          <Box display="flex" justifyContent="center" py={2}>
            <CircularProgress size={22} />
          </Box>
        )}
        {numericId != null && (
          <MenuItem onClick={() => onConfirm(numericId)}>
            <ListItemText primary={`Match to funeral #${numericId}`} />
          </MenuItem>
        )}
        <Table size="small">
          <TableBody>
            {results.map((r) => (
              <TableRow key={r.id} hover sx={{ cursor: 'pointer' }} onClick={() => onConfirm(r.id)}>
                <TableCell sx={{ width: 70 }}>#{r.id}</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>{r.name}</TableCell>
                <TableCell align="right">{formatDate(r.date_of_death)}</TableCell>
              </TableRow>
            ))}
            {!searching && query.trim().length >= 2 && results.length === 0 && numericId == null && (
              <TableRow>
                <TableCell colSpan={3} align="center">
                  <Typography variant="body2" color="text.secondary" py={2}>
                    No cases found
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );
};

// --- edit partner dialog ----------------------------------------------------

interface PartnerOption {
  id: string;
  name: string;
  type: string;
}

const EditPartnerDialog = ({
  open,
  invoice,
  onClose,
  onConfirm,
}: {
  open: boolean;
  invoice: PartnerInvoice;
  onClose: () => void;
  onConfirm: (body: { partner_id: string | null; partner_type: PartnerType }) => void;
}) => {
  const [partnerType, setPartnerType] = useState<PartnerType>(
    invoice.partner_type || 'transport',
  );
  const [partnerId, setPartnerId] = useState<string>(invoice.partner_id || '');

  useEffect(() => {
    if (open) {
      setPartnerType(invoice.partner_type || 'transport');
      setPartnerId(invoice.partner_id || '');
    }
  }, [open, invoice.partner_type, invoice.partner_id]);

  const { data: partners, isLoading } = useGetList<PartnerOption>('partners', {
    filter: { [PARTNER_TYPE_ROLE_FIELD[partnerType]]: true },
    pagination: { page: 1, perPage: 200 },
    sort: { field: 'name', order: 'ASC' },
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Set partner</DialogTitle>
      <DialogContent>
        <Box display="flex" flexDirection="column" gap={2} mt={1}>
          <ToggleButtonGroup
            exclusive
            color="primary"
            value={partnerType}
            onChange={(_, v) => {
              if (v) {
                setPartnerType(v);
                setPartnerId('');
              }
            }}
            fullWidth
          >
            <ToggleButton value="transport">Transport</ToggleButton>
            <ToggleButton value="crematorium">Crematorium</ToggleButton>
            <ToggleButton value="dropoff_location">Drop-off</ToggleButton>
          </ToggleButtonGroup>
          <FormControl fullWidth>
            <InputLabel id="edit-partner-label">Partner</InputLabel>
            <Select
              labelId="edit-partner-label"
              label="Partner"
              value={partnerId}
              onChange={(e) => setPartnerId(e.target.value)}
              disabled={isLoading}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {(partners || []).map((p) => (
                <MenuItem key={p.id} value={p.id}>
                  {p.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={() => onConfirm({ partner_id: partnerId || null, partner_type: partnerType })}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// --- mark paid dialog -------------------------------------------------------

const MarkPaidDialog = ({
  open,
  onClose,
  onConfirm,
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: (body: { payment_method: 'cheque' | 'credit_card'; payment_reference?: string; paid_at?: string }) => void;
}) => {
  const [method, setMethod] = useState<'cheque' | 'credit_card'>('cheque');
  const [reference, setReference] = useState('');

  useEffect(() => {
    if (open) {
      setMethod('cheque');
      setReference('');
    }
  }, [open]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Mark invoice paid</DialogTitle>
      <DialogContent>
        <Box display="flex" flexDirection="column" gap={2} mt={1}>
          <ToggleButtonGroup exclusive color="primary" value={method} onChange={(_, v) => v && setMethod(v)} fullWidth>
            <ToggleButton value="cheque">Cheque</ToggleButton>
            <ToggleButton value="credit_card">Credit card</ToggleButton>
          </ToggleButtonGroup>
          <TextField
            label={method === 'cheque' ? 'Cheque number (optional)' : 'Reference (optional)'}
            value={reference}
            onChange={(e) => setReference(e.target.value)}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          startIcon={<PaidIcon />}
          onClick={() => onConfirm({ payment_method: method, payment_reference: reference || undefined })}
        >
          Mark paid
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InvoiceDetailPage;
